/**
 * GoldTypeBreakdown component - Visual breakdown of gold holdings by type
 * Displays each gold type with progress bar and percentage
 */

import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

interface GoldTypeData {
  name: string
  quantity: number // in lượng
  percent: number
}

interface GoldTypeBreakdownProps {
  data: GoldTypeData[]
  totalQuantity: number
  className?: string
  maxItems?: number
}

export function GoldTypeBreakdown({
  data,
  totalQuantity,
  className,
  maxItems = 5,
}: GoldTypeBreakdownProps) {
  if (!data || data.length === 0 || totalQuantity <= 0) {
    return (
      <div className={cn('text-xs sm:text-sm text-muted-foreground', className)}>
        Chưa có dữ liệu
      </div>
    )
  }

  const displayData = data.slice(0, maxItems)
  const remaining = data.length - maxItems

  // Color palette for progress bars
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-orange-500',
    'bg-teal-500',
  ]

  return (
    <div className={cn('space-y-2', className)}>
      {displayData.map((item, index) => (
        <div key={item.name} className="space-y-1">
          <div className="flex items-center justify-between gap-2 text-xs sm:text-sm">
            <span className="font-medium text-foreground truncate flex-1 min-w-0">
              {item.name}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-muted-foreground">
                {item.quantity.toLocaleString('vi-VN', {
                  maximumFractionDigits: 2,
                })}{' '}
                lượng
              </span>
              <span className="font-semibold text-foreground min-w-[3rem] text-right">
                {item.percent.toFixed(1)}%
              </span>
            </div>
          </div>
          <Progress
            value={item.percent}
            className={cn('h-2', colors[index % colors.length])}
          />
        </div>
      ))}
      {remaining > 0 && (
        <div className="text-xs sm:text-sm text-muted-foreground pt-1">
          +{remaining} loại khác
        </div>
      )}
    </div>
  )
}

